import { normalizePhoneNumberToE164 } from '@/utils/phone'

// Dialpad Mini Dialer (CTI) postMessage API, v1.0:
// https://developers.dialpad.com/docs/dialpad-mini-dialer

export const DIALPAD_CTI_ORIGIN = 'https://dialpad.com'
export const DIALPAD_CTI_ALLOW = 'microphone; speaker-selection; autoplay; camera; display-capture; hid'
export const DIALPAD_CTI_SANDBOX = 'allow-popups allow-scripts allow-same-origin allow-forms'
export const CUSTOM_DATA_MAX_LENGTH = 2000

const CTI_API = 'opencti_dialpad'
const CTI_VERSION = '1.0'

// Issued by Dialpad under the older allowed-origins setup (/apps/<ClientID>). The current docs embed
// /apps/open without one. Not a secret: it is visible in the iframe src.
const DIALPAD_CTI_CLIENT_ID = 'Qaxa9DCGwpASXp4SCM9D2CTQb'

export type CtiIdentityType = 'Office' | 'OfficeGroup' | 'CallCenter'

export interface CtiParty {
  phone?: string
  type?: string
  id?: number | string
  email?: string
  name?: string
}

export interface CtiUserAuthentication {
  user_authenticated: boolean
  user_id: number
}

export interface CtiCallRinging {
  state: 'on' | 'off'
  id: number
  contact?: CtiParty
  target?: CtiParty
  internal_number?: string
  external_number?: string
}

export interface CtiInitiateCallPayload {
  phone_number: string
  enable_current_tab?: boolean
  identity_type?: CtiIdentityType
  identity_id?: number
  custom_data?: string
  outbound_caller_id?: string
}

export type CtiOutboundMethod = 'initiate_call' | 'enable_current_tab' | 'hang_up_all_calls'

export interface CtiCommand {
  api: typeof CTI_API
  version: typeof CTI_VERSION
  method: CtiOutboundMethod
  payload?: CtiInitiateCallPayload
}

export type CtiInboundMessage =
  | { method: 'user_authentication'; payload: CtiUserAuthentication }
  | { method: 'call_ringing'; payload: CtiCallRinging }

export interface InitiateCallRequest {
  phoneNumber: string
  identityType?: CtiIdentityType
  identityId?: number
  customData?: string
  outboundCallerId?: string
}

export const dialpadCtiUrl = (useClientId: boolean): string =>
  `${DIALPAD_CTI_ORIGIN}/apps/${useClientId ? DIALPAD_CTI_CLIENT_ID : 'open'}`

export const buildCtiCommand = (method: CtiOutboundMethod, payload?: CtiInitiateCallPayload): CtiCommand => ({
  api: CTI_API,
  version: CTI_VERSION,
  method,
  ...(payload ? { payload } : {}),
})

/** US/Canada E.164 (+1XXXXXXXXXX) to its 10 digits for customer search; other numbers as bare digits. */
export const toNationalDigits = (e164: string | undefined | null): string | null => {
  const digits = (e164 || '').replace(/\D/g, '')
  if (!digits) {
    return null
  }
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
}

/** Builds the initiate_call payload, or throws when Dialpad would reject the request. */
export const buildInitiateCallPayload = (request: InitiateCallRequest): CtiInitiateCallPayload => {
  const phoneNumber = normalizePhoneNumberToE164(request.phoneNumber)
  if (!phoneNumber) {
    throw new Error('A phone number is required to place a call')
  }
  if (request.identityType && request.identityId == null) {
    throw new Error(`identityId is required when calling as ${request.identityType}`)
  }
  if (request.customData && request.customData.length > CUSTOM_DATA_MAX_LENGTH) {
    throw new Error(`customData is ${request.customData.length} characters; Dialpad allows ${CUSTOM_DATA_MAX_LENGTH}`)
  }
  return {
    phone_number: phoneNumber,
    ...(request.identityType ? { identity_type: request.identityType, identity_id: request.identityId } : {}),
    ...(request.customData ? { custom_data: request.customData } : {}),
    ...(request.outboundCallerId ? { outbound_caller_id: request.outboundCallerId } : {}),
  }
}

/**
 * Returns the message if it came from our CTI frame, or null. The CTI posts to its parent with
 * target origin '*', so any frame or window can send look-alike messages; check both the origin and
 * that it is our frame's window.
 */
export const parseCtiMessage = (event: MessageEvent, ctiWindow: Window | null): CtiInboundMessage | null => {
  const data = event.data
  if (!data || data.api !== CTI_API) {
    return null
  }
  if (event.origin !== DIALPAD_CTI_ORIGIN || !ctiWindow || event.source !== ctiWindow) {
    console.warn('[dialpad:cti] ignored CTI-shaped message from', event.origin)
    return null
  }
  if (data.method === 'user_authentication' || data.method === 'call_ringing') {
    return { method: data.method, payload: data.payload }
  }
  return null
}
